<template>
  <BaseScreen v-bind="screen">
    <BaseMenu
      v-if="hasAdminPermissions"
      :items="menuItems"
      data-animation="fadeIn"
    />
    <BaseForm
      v-else
      ref="form"
      v-bind="form"
      class="screen__form"
      @submit="checkPassword"
    />
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
  </BaseScreen>
</template>

<script>
import axios from 'axios';

import screenMixin from '@/mixins/screen';
import formMixin from '@/mixins/form';
import terminalMixin from '@/mixins/terminal';

import BaseMenu from '@/components/BaseMenu.vue';
import FieldPassword from '@/components/FieldPassword.vue';

export default {
  name: 'AdminScreen',
  mixins: [screenMixin, formMixin, terminalMixin],
  components: {
    BaseMenu,
  },
  data() {
    return {
      errorCode: 0,
      errorMessage: '',
      hasAdminPermissions: false,
      config: null,
    };
  },
  computed: {
    titleText() {
      return this.$t('titles.admin');
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        // title: {
        //   text: String(this.errorCode),
        //   size: 'l',
        // },
        description: {
          text: this.errorMessage,
          size: 'l',
        },
        actions: [
          this.hasAdminPermissions ? {
            text: this.$t('actions.backToMenu'),
            action: this.hideError,
          } : null,
          this.config ? {
            text: this.$t('actions.tryAgain'),
            action: this.retryAuth,
          } : null,
        ].filter(Boolean),
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        description: {
          text: this.$t('titles.operationComplete'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.backToMenu'),
            action: this.hideThanks,
          },
        ],
      };
    },
    form() {
      return {
        fields: [
          {
            is: FieldPassword,
            name: 'password',
            minLength: 1,
          },
        ],
        actions: [
          {
            type: 'submit',
            text: this.$t('actions.enter'),
          },
        ],
      };
    },
    menuItems() {
      return [
        {
          icon: {
            name: 'open',
            theme: 'secondary',
            size: 'l',
          },
          label: {
            text: this.$t('actions.openSession'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.openSession,
        },
        {
          icon: {
            name: 'lock',
            theme: 'secondary',
            size: 'l',
          },
          label: {
            text: this.$t('actions.closeSession'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.closeSession,
        },
        {
          icon: {
            name: 'home',
            theme: 'plain',
            size: 'l',
          },
          label: {
            text: this.$t('actions.home'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToHomeScreen,
        },
      ];
    },
  },
  created() {
    this.fetchConfig();
  },
  methods: {
    checkPassword({ password }) {
      if (!this.config?.admin_password || password !== this.config?.admin_password) {
        this.onTerminalError(403, this.$t('titles.wrongPassword'));
        return;
      }
      this.hasAdminPermissions = true;
    },
    openSession() {
      if (this.isPending) return;
      this.openTerminalSession(); // функция из миксина
      this.showPending();
    },
    closeSession() {
      if (this.isPending) return;
      this.closeTerminalSession(); // функция из миксина
      this.showPending();
    },
    onTerminalSessionOpen() {
      // Этот колбек будет вызван из миксина после открытия сессии
      this.showThanks();
    },
    onTerminalSessionClose() {
      // Этот колбек будет вызван из миксина после закрытия сессии
      this.showThanks();
    },
    onTerminalPaperOut() {
      // Этот колбек будет вызван из миксина, если кончилась бумага
      this.onTerminalError(500, this.$t('titles.paperOut'));
    },
    onTerminalError(code, message) {
      // Этот колбек будет вызван из миксина, если прилетит ошибка
      this.errorCode = code;
      this.errorMessage = message;
      this.showError();
    },
    retryAuth() {
      this.hideError();
      this.$nextTick(this.setFieldFocus);
    },
    async fetchConfig() {
      try {
        const { data: config } = await axios.get(process.env.VUE_APP_CONFIG_URL);
        this.config = config;
      } catch (e) {
        console.error('fetchConfig error: ', e);
        this.errorMessage = 'No config error';
        this.showError();
      }
    },
  },
};
</script>
